<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import SettingsPageLayout from 'dashboard/components-next/captain/pageComponents/assistant/settings/SettingsPageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const assistantId = computed(() => route.params.assistantId);
const salvando = ref(false);
const resultado = ref(null);

const TONS = ['proximo', 'profissional', 'acolhedor'];
const TAMANHOS = ['curta', 'media'];

const form = ref({
  empresa: '',
  o_que_faz: '',
  objetivo: '',
  publico: '',
  como_se_apresenta: '',
  tom: 'proximo',
  tamanho_da_resposta: 'curta',
  descobrir_antes: '',
  sempre_fazer: '',
  nunca: '',
  chamar_pessoa: '',
});

const emLinhas = texto =>
  (texto || '')
    .split('\n')
    .map(linha => linha.trim())
    .filter(Boolean);

const montar = async () => {
  salvando.value = true;
  try {
    const { data } = await CaptainAssistantAPI.montar({
      assistantId: assistantId.value,
      montagem: {
        ...form.value,
        descobrir_antes: emLinhas(form.value.descobrir_antes),
        sempre_fazer: emLinhas(form.value.sempre_fazer),
        nunca: emLinhas(form.value.nunca),
        chamar_pessoa: emLinhas(form.value.chamar_pessoa),
      },
    });
    resultado.value = data;
    useAlert(t('CAPTAIN.ASSISTANTS.ASSEMBLY.SAVED'));
  } catch (e) {
    useAlert(t('CAPTAIN.ASSISTANTS.ASSEMBLY.ERROR'));
  } finally {
    salvando.value = false;
  }
};

// Volta as respostas anteriores, para ajustar sem responder tudo de novo.
const carregar = async () => {
  try {
    const { data } = await CaptainAssistantAPI.show(assistantId.value);
    const guardado = data?.config?.montagem;
    if (!guardado) return;
    Object.keys(form.value).forEach(chave => {
      const valor = guardado[chave];
      if (valor === undefined || valor === null) return;
      form.value[chave] = Array.isArray(valor) ? valor.join('\n') : valor;
    });
  } catch (e) {
    // Sem respostas guardadas o formulário simplesmente começa em branco.
  }
};

onMounted(carregar);

const classeCampo =
  'w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10';
</script>

<template>
  <SettingsPageLayout
    :heading="t('CAPTAIN.ASSISTANTS.ASSEMBLY.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.ASSEMBLY.DESCRIPTION')"
  >
    <div class="flex flex-col w-full gap-6">
      <section class="flex flex-col gap-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.TITLE') }}
        </h3>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.COMPANY') }}
          </span>
          <input
            v-model="form.empresa"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.COMPANY_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.WHAT') }}
          </span>
          <textarea
            v-model="form.o_que_faz"
            rows="2"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.WHAT_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.GOAL') }}
          </span>
          <input
            v-model="form.objetivo"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.GOAL_PLACEHOLDER')
            "
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.GOAL_HELP') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.AUDIENCE') }}
          </span>
          <input
            v-model="form.publico"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUSINESS.AUDIENCE_PLACEHOLDER')
            "
          />
        </label>
      </section>

      <section class="flex flex-col gap-4 pt-4 border-t border-n-weak">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.TITLE') }}
        </h3>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.GREETING') }}
          </span>
          <input
            v-model="form.como_se_apresenta"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.GREETING_PLACEHOLDER')
            "
          />
        </label>

        <div class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.TONE') }}
            </span>
            <select v-model="form.tom" :class="classeCampo">
              <option v-for="tom in TONS" :key="tom" :value="tom">
                {{
                  t(
                    `CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.TONES.${tom.toUpperCase()}`
                  )
                }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.LENGTH') }}
            </span>
            <select v-model="form.tamanho_da_resposta" :class="classeCampo">
              <option
                v-for="tamanho in TAMANHOS"
                :key="tamanho"
                :value="tamanho"
              >
                {{
                  t(
                    `CAPTAIN.ASSISTANTS.ASSEMBLY.VOICE.LENGTHS.${tamanho.toUpperCase()}`
                  )
                }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <section class="flex flex-col gap-4 pt-4 border-t border-n-weak">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.TITLE') }}
        </h3>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.DISCOVER') }}
          </span>
          <textarea
            v-model="form.descobrir_antes"
            rows="3"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.DISCOVER_PLACEHOLDER')
            "
          />
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.ONE_PER_LINE') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.ALWAYS') }}
          </span>
          <textarea
            v-model="form.sempre_fazer"
            rows="2"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.ALWAYS_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.NEVER') }}
          </span>
          <textarea
            v-model="form.nunca"
            rows="3"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.NEVER_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.HUMAN') }}
          </span>
          <textarea
            v-model="form.chamar_pessoa"
            rows="2"
            :class="classeCampo"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.ASSEMBLY.RULES.HUMAN_PLACEHOLDER')
            "
          />
        </label>
      </section>

      <div class="flex flex-wrap items-center gap-3 pt-2">
        <Button
          :label="t('CAPTAIN.ASSISTANTS.ASSEMBLY.BUILD')"
          :is-loading="salvando"
          @click="montar"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.OVERWRITE_WARNING') }}
        </span>
      </div>

      <section
        v-if="resultado"
        class="flex flex-col gap-3 pt-4 border-t border-n-weak"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.ASSEMBLY.RESULT.TITLE') }}
        </h3>
        <div class="p-4 rounded-lg bg-n-alpha-black2">
          <p class="text-sm text-n-slate-12">
            {{ resultado.identidade.descricao }}
          </p>
        </div>
        <p class="text-sm text-n-slate-11">
          {{
            t('CAPTAIN.ASSISTANTS.ASSEMBLY.RESULT.COUNTS', {
              guidelines: resultado.diretrizes.length,
              guardrails: resultado.protecoes.length,
            })
          }}
        </p>
      </section>
    </div>
  </SettingsPageLayout>
</template>
